import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { UserMessage } from "@sendbird/chat/message";
import { ReadStatus } from "@sendbird/chat/groupChannel";
import { MessageItem, useGetMessageList } from "@src/hooks/auctionAgent/chat";

import { useSearchParams } from "react-router-dom";
interface SendBirdChatContextType {
    sendBirdMessages: UserMessage[];
    setSendBirdMessages: React.Dispatch<React.SetStateAction<UserMessage[]>>;
    msgMessages: MessageItem[];
    setMsgMessages: React.Dispatch<React.SetStateAction<MessageItem[]>>;
    readStatusMap: Record<string, ReadStatus>;
    setReadStatusMap: React.Dispatch<React.SetStateAction<Record<string, ReadStatus>>>;
    requestedMorePrevMsgs: {
        hasNextPage: boolean;
        requested: boolean;
    };
    setRequestedMorePrevMsgs: React.Dispatch<React.SetStateAction<{ hasNextPage: boolean; requested: boolean }>>;
}

const SendBirdChatContext = createContext<SendBirdChatContextType | undefined>(undefined);
const PREV_CHAT_MESSAGES_LIMIT = 15;

export const SendBirdChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [searchParams] = useSearchParams();
    const chattingRoomNo = searchParams.get("chattingRoomNo");
    const [requestedMorePrevMsgs, setRequestedMorePrevMsgs] = useState({
        hasNextPage: false,
        requested: false,
    });
    const [msgMessages, setMsgMessages] = useState<MessageItem[]>([]); // 실시간이 아닌 과거 메시지는 db 조회
    const [sendBirdMessages, setSendBirdMessages] = useState<UserMessage[]>([]); // 실시간 메시지
    const [readStatusMap, setReadStatusMap] = useState<Record<string, ReadStatus>>({});
    const {
        data: savedMessageList,
        fetchNextPage,
        hasNextPage,
    } = useGetMessageList({
        chattingRoomNo: Number(chattingRoomNo),
        cursor: "",
        limit: PREV_CHAT_MESSAGES_LIMIT,
    });

    useEffect(() => {
        if (requestedMorePrevMsgs.hasNextPage && requestedMorePrevMsgs.requested) {
            fetchNextPage();
            // setRequestedMorePrevMsgs({ ...requestedMorePrevMsgs, requested: false });
        }
    }, [requestedMorePrevMsgs]);

    useEffect(() => {
        if (savedMessageList?.pages.length && savedMessageList.pages.length > 0) {
            setMsgMessages(savedMessageList.pages);
        }

        if (hasNextPage) {
            setRequestedMorePrevMsgs({ ...requestedMorePrevMsgs, hasNextPage: true });
        } else {
            setRequestedMorePrevMsgs({ ...requestedMorePrevMsgs, hasNextPage: false });
        }
    }, [savedMessageList, hasNextPage]);

    return (
        <SendBirdChatContext.Provider
            value={{
                sendBirdMessages,
                setSendBirdMessages,
                readStatusMap,
                setReadStatusMap,
                msgMessages,
                setMsgMessages,
                requestedMorePrevMsgs,
                setRequestedMorePrevMsgs,
            }}
        >
            {children}
        </SendBirdChatContext.Provider>
    );
};

export const useSendBirdChat = () => {
    const context = useContext(SendBirdChatContext);
    if (context === undefined) {
        throw new Error("useSendBirdChat must be used within a SendBirdChatProvider");
    }
    return context;
};
